import { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { PublicCapTableOverview } from '../../../hooks/shared-cap-table/shared-cap-table.types';
import { StringKey } from '../../../lang/index';
import { SummaryItem } from '../../cap-table/summary-item';

type PublicCapTableOverviewProps = {
  overview: PublicCapTableOverview;
  stakeholdersCount: number;
};

type CardKey = 'stakeholders' | keyof PublicCapTableOverview;

const CARDS: [CardKey, StringKey, 'currency' | 'number'][] = [
  ['postMoneyValuation', 'POST_MONEY_VALUATION', 'currency'],
  ['investorsCount', 'INVESTORS', 'number'],
  ['issuedShares', 'ISSUED_SHARES', 'number'],
  ['lifetimeInvestmentEquity', 'SHARE_PUBLIC_LIFETIME_EQUITY_INVESTMENT', 'currency'],
  ['stakeholders', 'SHARE_PUBLIC_STAKEHOLDERS', 'number'],
  ['dilutedShares', 'DILUTED_SHARES', 'number'],
];

/**
 * The block is a grey shell carrying its own heading over a white card, the shape the design uses
 * for every panel on the page and the one the authorised cap table already builds — down to
 * `SummaryItem`, so the figures a viewer sees are set exactly as the owner's own are.
 */
export const PublicCapTableOverviewCards: FC<PublicCapTableOverviewProps> = ({
  overview,
  stakeholdersCount,
}) => {
  const valueOf = (key: CardKey): number =>
    key === 'stakeholders' ? stakeholdersCount : overview[key];

  return (
    <div className="flex h-full min-h-full flex-col rounded-lg bg-gray-50 p-1">
      <div className="px-4 py-3">
        <span className="text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id="OVERALL_STATUS" />
        </span>
      </div>

      {/* A grid, not the app's wrapping flex row: this page gives the block no height to wrap
          against, so a column-direction wrap spilled the tiles sideways off the screen. */}
      <div className="grid w-full flex-1 grid-cols-1 content-start gap-3 rounded-md bg-white p-3 shadow-sm xs:grid-cols-2">
        {CARDS.map(([key, title, format]) => (
          <SummaryItem
            key={key}
            title={<AppFormattedMessage id={title} />}
            value={`${format === 'currency' ? '$ ' : ''}${valueOf(key).toLocaleString('en-US')}`}
          />
        ))}
      </div>
    </div>
  );
};
